import React from 'react'

// Dependency-free responsive bar chart (the admin portal has no charting lib
// and the deploy runs `npm ci`, so a new dependency isn't an option). Renders
// an SVG with a nice y-axis, value labels and rotated x-axis labels. Preserves
// the row order it is given (queries return rows chronologically).

const VB_W = 1000
const VB_H = 300
const PAD = { top: 22, right: 16, bottom: 78, left: 48 }

// Round a raw max up to a clean axis maximum (1/2/5 * 10^n).
const niceMax = (value) => {
  if (!value || value <= 0) return 10
  const pow = Math.pow(10, Math.floor(Math.log10(value)))
  const n = value / pow
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * pow
}

const BarChart = ({ rows, xKey, yKey, color = '#5b9bd5' }) => {
  const data = (rows || []).map((r) => ({
    x: r[xKey] == null ? '' : String(r[xKey]).trim(),
    y: Number(r[yKey]) || 0
  }))
  if (!data.length) return null

  const innerW = VB_W - PAD.left - PAD.right
  const innerH = VB_H - PAD.top - PAD.bottom
  const maxY = niceMax(Math.max(...data.map((d) => d.y)))
  const slot = innerW / data.length
  const barW = Math.min(42, slot * 0.66)
  const ticks = 4
  // With many months, thin out x-labels so they don't overlap.
  const labelEvery = Math.ceil(data.length / 24)

  const yFor = (v) => PAD.top + innerH - (v / maxY) * innerH

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {/* y gridlines + labels */}
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const val = (maxY / ticks) * i
        const y = yFor(val)
        return (
          <g key={`g${i}`}>
            <line
              x1={PAD.left}
              x2={VB_W - PAD.right}
              y1={y}
              y2={y}
              stroke="#eceff1"
            />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#90a4ae">
              {Math.round(val).toLocaleString()}
            </text>
          </g>
        )
      })}

      {/* bars + value labels + x labels */}
      {data.map((d, i) => {
        const cx = PAD.left + i * slot + slot / 2
        const h = (d.y / maxY) * innerH
        const y = PAD.top + innerH - h
        const showLabel = i % labelEvery === 0
        return (
          <g key={`b${i}`}>
            <rect x={cx - barW / 2} y={y} width={barW} height={h} fill={color} rx="2" />
            <text x={cx} y={y - 5} textAnchor="middle" fontSize="10" fill="#546e7a">
              {d.y.toLocaleString()}
            </text>
            {showLabel ? (
              <text
                x={cx}
                y={PAD.top + innerH + 14}
                textAnchor="end"
                fontSize="10"
                fill="#607d8b"
                transform={`rotate(-45 ${cx} ${PAD.top + innerH + 14})`}
              >
                {d.x}
              </text>
            ) : null}
          </g>
        )
      })}

      {/* baseline */}
      <line
        x1={PAD.left}
        x2={VB_W - PAD.right}
        y1={PAD.top + innerH}
        y2={PAD.top + innerH}
        stroke="#b0bec5"
      />
    </svg>
  )
}

export default BarChart
